import { formatMontant } from '../utils/montants';
import { coutMensuelLigne, afficheQuantite } from '../utils/coutsRecurrents';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Divider,
  Chip,
  LinearProgress,
  Grid,
  Tooltip
} from '@mui/material';
import {
  Clock,
  DollarSign,
  TrendingUp,
  Calendar,
  PieChart,
  CheckCircle,
  XCircle,
  Package,
  AlertTriangle
} from 'lucide-react';
import { useSocket } from '../context/SocketContext';

// Color palette for dynamic hour types
const hourTypeColors = [
  '#3b82f6', '#10b981', '#f97316', '#8b5cf6', '#ec4899',
  '#06b6d4', '#eab308', '#ef4444', '#14b8a6', '#6366f1'
];

function QuoteSummary({ submissionId }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const { socket } = useSocket();

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${submissionId}/quote/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Fetch summary error:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSummary();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchSummary, 30000);
    return () => clearInterval(interval);
  }, [submissionId]);

  // Listen to WebSocket events for quote changes
  useEffect(() => {
    if (!socket) return;

    const handleQuoteChanged = (data) => {
      if (data.submissionId === submissionId) {
        fetchSummary();
      }
    };

    socket.on('quoteChanged', handleQuoteChanged);

    return () => {
      socket.off('quoteChanged', handleQuoteChanged);
    };
  }, [socket, submissionId]);

  if (loading) {
    return (
      <Paper sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">Calcul en cours...</Typography>
      </Paper>
    );
  }

  if (!summary) {
    return null;
  }

  const { totals, sections, recurringCosts, quoteProducts } = summary;
  
  // Use dynamic hours breakdown (new system) or fall back to legacy
  const dynamicHours = totals.dynamicHoursBreakdown || {};
  const hasDynamicHours = Object.keys(dynamicHours).length > 0;

  return (
    <Stack spacing={2}>
      {/* Main Summary Card */}
      <Paper sx={{ 
        p: 3, 
        bgcolor: 'rgba(0, 212, 170, 0.03)', 
        border: '1px solid rgba(0, 212, 170, 0.15)', 
        borderRadius: 2 
      }}>
        <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5, fontWeight: 'bold', letterSpacing: 0.5 }}>
          RÉSUMÉ FINANCIER
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Montants estimés avant taxes
        </Typography>

        <Grid container spacing={3}>
          {/* Total Development */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: 1, 
                bgcolor: 'rgba(0, 212, 170, 0.1)', 
                display: 'flex' 
              }}>
                <DollarSign size={20} color="#00d4aa" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Temps
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#00d4aa' }}>
                  ${totals.developmentPrice?.toLocaleString() || 0}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Total Hours */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: 1, 
                bgcolor: 'rgba(255, 255, 255, 0.05)', 
                display: 'flex' 
              }}>
                <Clock size={20} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Heures confirmées
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {totals.hours || 0}h
                </Typography>
                {/* Optional hours info */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {totals.hoursOptional > 0 && (
                    <Typography variant="caption" sx={{ color: '#f59e0b' }}>
                      + {totals.hoursOptional}h en attente
                    </Typography>
                  )}
                  {totals.hoursIncluded > 0 && (
                    <Typography variant="caption" sx={{ color: '#10b981' }}>
                      + {totals.hoursIncluded}h offertes
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Products / Material */}
          {(totals.productsPrice > 0 || totals.productsPriceOptional > 0 || totals.productsPriceIncluded > 0) && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ opacity: 0.1 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: 1, 
                    bgcolor: 'rgba(219, 39, 119, 0.1)', 
                    display: 'flex' 
                  }}>
                    <Package size={20} color="#db2777" />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Matériel / Produits
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#db2777' }}>
                      ${totals.productsPrice?.toLocaleString() || 0}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {totals.acceptedProductsCount > 0 && (
                    <Typography variant="caption" sx={{ color: '#10b981' }}>
                      dont {totals.acceptedProductsCount} produit{totals.acceptedProductsCount > 1 ? 's' : ''} accepté{totals.acceptedProductsCount > 1 ? 's' : ''}
                    </Typography>
                  )}
                  {totals.productsPriceOptional > 0 && (
                    <Typography variant="caption" sx={{ color: '#f59e0b' }}>
                      + ${totals.productsPriceOptional?.toLocaleString()} en attente
                    </Typography>
                  )}
                  {totals.productsPriceIncluded > 0 && (
                    <Typography variant="caption" sx={{ color: '#10b981' }}>
                      + ${totals.productsPriceIncluded?.toLocaleString()} offert
                    </Typography>
                  )}
                </Box>
              </Grid>
            </>
          )}

          {/* Monthly Costs */}
          {(totals.monthlyRequired > 0 || totals.monthlyOptional > 0 || totals.includedCostsCount > 0) && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ opacity: 0.1 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: 1, 
                    bgcolor: 'rgba(245, 158, 11, 0.1)', 
                    display: 'flex' 
                  }}>
                    <Calendar size={20} color="#f59e0b" />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Coûts mensuels
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                      ${totals.monthlyTotal?.toLocaleString() || 0}/mois
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {totals.acceptedCostsCount > 0 && (
                    <Typography variant="caption" sx={{ color: '#10b981' }}>
                      dont {totals.acceptedCostsCount} option{totals.acceptedCostsCount > 1 ? 's' : ''} acceptée{totals.acceptedCostsCount > 1 ? 's' : ''}
                    </Typography>
                  )}
                  {totals.monthlyOptional > 0 && (
                    <Typography variant="caption" sx={{ color: '#f59e0b' }}>
                      + ${totals.monthlyOptional?.toLocaleString()}/mois en attente
                    </Typography>
                  )}
                  {totals.includedCostsCount > 0 && (
                    <Typography variant="caption" sx={{ color: '#10b981' }}>
                      + {totals.includedCostsCount} service{totals.includedCostsCount > 1 ? 's' : ''} inclus
                    </Typography>
                  )}
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Hours Breakdown - Dynamic system */}
      {totals.hours > 0 && hasDynamicHours && (
        <Paper sx={{ 
          p: 2.5, 
          bgcolor: 'rgba(255, 255, 255, 0.02)', 
          border: '1px solid rgba(255, 255, 255, 0.06)', 
          borderRadius: 2 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PieChart size={16} color="#00d4aa" />
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Répartition des heures
            </Typography>
          </Box>

          <Stack spacing={1.5}>
            {Object.entries(dynamicHours)
              .filter(([_, data]) => data.hours > 0)
              .sort((a, b) => b[1].hours - a[1].hours)
              .map(([slug, data], idx) => {
                const hours = data.hours || 0;
                const percentage = totals.hours > 0 ? (hours / totals.hours) * 100 : 0;
                const color = hourTypeColors[idx % hourTypeColors.length];

                return (
                  <Box key={slug}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          bgcolor: color 
                        }} />
                        <Typography variant="body2">{data.name || slug}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {hours}h ({Math.round(percentage)}%)
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        '& .MuiLinearProgress-bar': { 
                          bgcolor: color,
                          borderRadius: 3
                        }
                      }}
                    />
                  </Box>
                );
              })}
          </Stack>
        </Paper>
      )}

      {/* Section Breakdown */}
      {sections?.length > 0 && (
        <Paper sx={{ 
          p: 2.5, 
          bgcolor: 'rgba(255, 255, 255, 0.02)', 
          border: '1px solid rgba(255, 255, 255, 0.06)', 
          borderRadius: 2 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TrendingUp size={16} color="#00d4aa" />
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Par section
            </Typography>
          </Box>

          <Stack spacing={1}>
            {sections.map((section) => (
              <Box 
                key={section.id}
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  py: 1,
                  px: 1.5,
                  bgcolor: section.isOptional && section.isAcceptedByClient === false 
                    ? 'rgba(239, 68, 68, 0.03)' 
                    : 'rgba(255, 255, 255, 0.01)',
                  borderRadius: 1,
                  opacity: section.isOptional && section.isAcceptedByClient === false ? 0.6 : 1,
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.03)' }
                }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {section.name}
                    </Typography>
                    {section.isIncluded && (
                      <Chip label="Offert" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }} />
                    )}
                    {section.isOptional && (
                      <Chip label="Optionnel" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }} />
                    )}
                    {section.isOptional && section.isAcceptedByClient === true && (
                      <Chip icon={<CheckCircle size={10} />} label="Accepté" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#10b981', '& .MuiChip-icon': { color: '#10b981' } }} />
                    )}
                    {section.isOptional && section.isAcceptedByClient === false && (
                      <Chip icon={<XCircle size={10} />} label="Refusé" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', '& .MuiChip-icon': { color: '#ef4444' } }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {section.items?.length || 0} éléments • {section.totalHours}h @ ${section.rate}/h
                  </Typography>
                </Box>
                <Chip
                  label={section.isIncluded ? 'Offert' : `$${section.totalPrice?.toLocaleString()}`}
                  size="small"
                  sx={{ 
                    fontWeight: 600,
                    bgcolor: section.isIncluded ? 'rgba(16, 185, 129, 0.1)' : 'rgba(0, 212, 170, 0.1)',
                    color: section.isIncluded ? '#10b981' : '#00d4aa',
                    textDecoration: section.isOptional && section.isAcceptedByClient === false ? 'line-through' : 'none'
                  }}
                />
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Products Detail */}
      {quoteProducts?.length > 0 && (
        <Paper sx={{ 
          p: 2.5, 
          bgcolor: 'rgba(255, 255, 255, 0.02)', 
          border: '1px solid rgba(255, 255, 255, 0.06)', 
          borderRadius: 2 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Package size={16} color="#db2777" />
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Matériel détaillé
            </Typography>
          </Box>

          <Stack spacing={1}>
            {quoteProducts.map((product) => {
              const productName = product.product?.name || product.customName || 'Produit';
              const lineTotal = product.quantity * product.unitPrice * (1 - (product.discount || 0) / 100);
              
              return (
                <Box 
                  key={product.id}
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 1,
                    px: 1.5,
                    bgcolor: product.isOptional && product.isAcceptedByClient === false 
                      ? 'rgba(239, 68, 68, 0.03)' 
                      : 'rgba(255, 255, 255, 0.01)',
                    borderRadius: 1,
                    opacity: product.isOptional && product.isAcceptedByClient === false ? 0.6 : 1
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {productName}
                      </Typography>
                      {product.isIncluded && (
                        <Chip 
                          label="Offert" 
                          size="small" 
                          sx={{ 
                            height: 18, 
                            fontSize: '0.6rem',
                            bgcolor: 'rgba(16, 185, 129, 0.15)',
                            color: '#10b981'
                          }} 
                        />
                      )}
                      {product.isOptional && (
                        <Chip 
                          label="Optionnel" 
                          size="small" 
                          sx={{ 
                            height: 18, 
                            fontSize: '0.6rem',
                            bgcolor: 'rgba(245, 158, 11, 0.15)',
                            color: '#f59e0b'
                          }} 
                        />
                      )}
                      {product.isOptional && product.isAcceptedByClient === true && (
                        <Chip 
                          icon={<CheckCircle size={10} />}
                          label="Accepté" 
                          size="small" 
                          sx={{ 
                            height: 18, 
                            fontSize: '0.6rem',
                            bgcolor: 'rgba(16, 185, 129, 0.15)',
                            color: '#10b981',
                            '& .MuiChip-icon': { color: '#10b981' }
                          }} 
                        />
                      )}
                      {product.isOptional && product.isAcceptedByClient === false && (
                        <Chip 
                          icon={<XCircle size={10} />}
                          label="Refusé" 
                          size="small" 
                          sx={{ 
                            height: 18, 
                            fontSize: '0.6rem',
                            bgcolor: 'rgba(239, 68, 68, 0.15)',
                            color: '#ef4444',
                            '& .MuiChip-icon': { color: '#ef4444' }
                          }} 
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {product.quantity} × ${product.unitPrice?.toLocaleString()}
                      {product.discount > 0 && ` (-${product.discount}%)`}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600, 
                      color: product.isIncluded ? '#10b981' : '#db2777',
                      textDecoration: product.isOptional && product.isAcceptedByClient === false ? 'line-through' : 'none'
                    }}
                  >
                    {product.isIncluded ? 'Offert' : `$${lineTotal.toLocaleString()}`}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* Recurring Costs Detail */}
      {recurringCosts?.length > 0 && (
        <Paper sx={{ 
          p: 2.5, 
          bgcolor: 'rgba(255, 255, 255, 0.02)', 
          border: '1px solid rgba(255, 255, 255, 0.06)', 
          borderRadius: 2 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Calendar size={16} color="#f59e0b" />
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Coûts récurrents détaillés
            </Typography>
          </Box>

          <Stack spacing={1}>
            {recurringCosts.map((cost) => (
              <Box 
                key={cost.id}
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  py: 1,
                  px: 1.5,
                  bgcolor: 'rgba(255, 255, 255, 0.01)',
                  borderRadius: 1
                }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {cost.name}
                    </Typography>
                    {cost.isIncluded && (
                      <Chip 
                        label="Inclus" 
                        size="small" 
                        sx={{ 
                          height: 18, 
                          fontSize: '0.6rem',
                          bgcolor: 'rgba(16, 185, 129, 0.15)',
                          color: '#10b981'
                        }} 
                      />
                    )}
                    {cost.isOptional && (
                      <Chip 
                        label="Optionnel" 
                        size="small" 
                        sx={{ 
                          height: 18, 
                          fontSize: '0.6rem',
                          bgcolor: 'rgba(245, 158, 11, 0.15)',
                          color: '#f59e0b'
                        }} 
                      />
                    )}
                    {cost.isOptional && cost.isAcceptedByClient === true && (
                      <Chip 
                        icon={<CheckCircle size={10} />}
                        label="Accepté" 
                        size="small" 
                        sx={{ 
                          height: 18, 
                          fontSize: '0.6rem',
                          bgcolor: 'rgba(16, 185, 129, 0.15)',
                          color: '#10b981',
                          '& .MuiChip-icon': { color: '#10b981' }
                        }} 
                      />
                    )}
                    {cost.isOptional && cost.isAcceptedByClient === false && (
                      <Chip 
                        icon={<XCircle size={10} />}
                        label="Refusé" 
                        size="small" 
                        sx={{ 
                          height: 18, 
                          fontSize: '0.6rem',
                          bgcolor: 'rgba(239, 68, 68, 0.15)',
                          color: '#ef4444',
                          '& .MuiChip-icon': { color: '#ef4444' }
                        }} 
                      />
                    )}
                  </Box>
                  {cost.description && (
                    <Typography variant="caption" color="text.secondary">
                      {cost.description}
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: cost.isIncluded ? '#10b981' : (cost.isOptional ? '#f59e0b' : '#f59e0b') }}>
                  {cost.isIncluded
                    ? 'Inclus'
                    : `${afficheQuantite(cost) ? `${cost.quantity} × $${formatMontant(cost.monthlyCost)}  ` : ''}$${formatMontant(coutMensuelLigne(cost))}/mois`}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Notes / Conditions */}
      <Paper sx={{ 
        p: 2.5, 
        bgcolor: 'rgba(245, 158, 11, 0.03)', 
        border: '1px solid rgba(245, 158, 11, 0.15)', 
        borderRadius: 2 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <AlertTriangle size={16} color="#f59e0b" />
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
            CONDITIONS
          </Typography>
        </Box>
        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            • Le prix de ce devis est valide pour une période de 30 jours à partir de la date d'émission.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            • Tout point non mentionné explicitement dans ce devis est considéré comme une exclusion.
          </Typography>
        </Stack>
      </Paper>
    </Stack>
  );
}

export default QuoteSummary;
